import React, { ReactElement, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useMasterAccountId } from '../../../hooks/redux';
import addAlert from 'ps-components/AlertToast/addAlert';
import AdvancedCell from 'ps-components/AdvancedTable/AdvancedCell';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import AdvancedRow from 'ps-components/AdvancedTable/AdvancedRow';
import ActionableHeaderCard from 'ps-components/Card/ActionableHeaderCard';
import Button from 'ps-components/Button/Button';
import CheckboxInput from 'ps-components/Input/CheckboxInput';
import ProgressStatusOverlay from 'ps-components/ProgressStatus/ProgressStatusOverlay';
import SlideoutFooter from 'ps-components/Slideout/Footer/SlideoutFooter';
import SlideoutHeader from 'ps-components/Slideout/Header/SlideoutHeader';
import SlideoutSubmitSuccess from 'ps-components/Slideout/SubmitSuccess/SlideoutSubmitSuccess';
import { COLUMN_HEADERS } from './constants';
import { getFormData } from './helpers';
import { renderNumberInput, renderSelect } from '../PrivateSurveyJobMatch/helpers';
import { getRegressionSurveyJobs, getRegressionSurveySources, getRegressionSurveyDatacuts, getRegressionSurveyMatches, saveRegressionSurveyMatches, submitRegressionSurveyMatches } from './RegressionSurveyJobMatchService';
import { IRegressionMatch, IRegressionDatacut, IRegressionJob, IRegressionSource, IRegressionMatchParams } from './types/regression';
import { ISlideout } from '../PrivateSurveyJobMatch/types/slideout';
import { IColumn, IRow } from './types/table';
import './RegressionSurveyJobMatch.scss';


interface ISelectItem {
  id: string;
  value: string;
}

const RegressionSurveyJobMatch = (props: {
  aging: string;
  benchmarkYear: number;
  currency: string;
  hideSlideout: () => void;
  orgJobId: number;
  slideout: ISlideout;
}): ReactElement => {
  const { aging, benchmarkYear, currency, hideSlideout, orgJobId, slideout } = props;
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [isValidScope, setIsValidScope] = useState<boolean>(false);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState<boolean>(false);
  const [selectedSurveySource, setSelectedSurveySource] = useState<IRegressionSource|undefined>(undefined);
  const [selectedSurveyJob, setSelectedSurveyJob] = useState<IRegressionJob|undefined>(undefined);
  const [selectedSurveyDatacut, setSelectedSurveyDatacut] = useState<IRegressionDatacut|undefined>(undefined);
  const [selectedSurveyScope, setSelectedSurveyScope] = useState<string>("");
  const [surveySourceOptions, setSurveySourceOptions] = useState<IRegressionSource[]>([]);
  const [surveyDatacutOptions, setSurveyDatacutOptions] = useState<IRegressionDatacut[]>([]);
  const [surveyJobOptions, setSurveyJobOptions] = useState<IRegressionJob[]>([]);
  const [regressionMatches, setRegressionMatches] = useState<IRegressionMatch[]>([]);
  const [selectedMatches, setSelectedMatches] = useState<string[]>([]);
  const isCalculateEnabled = isValidScope;
  const isSaveEnabled = selectedMatches.length > 0;
  const masterAccountId = useMasterAccountId();
  /** 
   * Moved minimumValue and maximumValue to useState to ensure re-render on each state change, 
   * enabling accurate validation message display.
  */
  const [minimumValue, setMinimumValue] = useState<any>("");
  const [maximumValue, setMaximumValue] = useState<any>("");

  useEffect(() => {
    if (!slideout.open) clearInputs();
    if (slideout.open) getSurveySources();
  }, [slideout.open]);

  const clearInputs = () => {
    setSelectedSurveyJob(undefined);
    setSelectedSurveySource(undefined)
    setSelectedSurveyDatacut(undefined);
    setSelectedSurveyScope("");
    setRegressionMatches([]);
    setSelectedMatches([]);
    setSurveySourceOptions([]);
    setIsSelectAllChecked(false);
    setIsValidScope(false);
    setIsSaved(false);
  }

  const calculateValues = async (useSaveMode: boolean, submit: boolean) => {
    if (selectedSurveySource && selectedSurveyJob && selectedSurveyDatacut && selectedSurveyScope) {
      let formData: any;
      let params = {
        survey_code: selectedSurveySource?.value,
        job_title: selectedSurveyJob?.job_code,
        job_code: selectedSurveyJob?.job_code,
        pulldown_pretty: selectedSurveyDatacut?.pulldown_pretty,
        input_scope_value: selectedSurveyScope,
        job_scope: selectedSurveyDatacut?.job_scope,
        volume_type: selectedSurveyDatacut?.volume_type
      } as IRegressionMatchParams;

      if (useSaveMode && selectedMatches.length > 0) {
        params = {
          ...params,
          active_survey_year: benchmarkYear,
          active_aging_code: aging,
          save_mode: useSaveMode,
          matched_job_code: selectedMatches,
          job_id: orgJobId
        }

        formData = getFormData(params);

        saveRegressionSurveyMatches(formData).then(resp => {
          if (submit) {
            submitRegressionSurveyMatches(formData).then(resp => {
              setIsSaved(true);
              addAlert('Successfully added selected match(es) to pricing', 'success', 5000, 'top-center');
            }).catch(e => {
              addAlert('Error adding selected match(es) to pricing', 'danger', 5000, 'top-center');
            });;
          } else {
            addAlert('Successfully saved selected row(s)', 'success', 5000, 'top-center');
          }
        }).catch(e => {
          addAlert('Error saving selected row(s)', 'danger', 5000, 'top-center');
        });
      } else if (!useSaveMode && regressionMatches.length === 0) {
        let jobs = await getRegressionSurveyMatches(params);
        setRegressionMatches(jobs.data.results);
      }
    }
  }

  const getSurveySources = async () => {
    let options = await getRegressionSurveySources(masterAccountId, { currency });
    setSurveySourceOptions(options?.data?.map((option: IRegressionSource) => {
      return { ...option, text: option.survey_code, value: option.survey_code, name: option.pub_survey_short_name }
    }));
  };

  const getSurveyJobs = async (code: string) => {
    let options = await getRegressionSurveyJobs(masterAccountId, { survey_code: code });
    setSurveyJobOptions(options?.data?.map((option: IRegressionJob) => {
      const displayName = getFormattedSurveyJobOption(option)
      return { ...option, text: displayName, value: displayName, jobCode: option.job_code }
    }));
  };

  const getFormattedSurveyJobOption = (option: IRegressionJob) => {
    return `${option.job_title} (${option.job_code})`
  }

  function onClose() {
    hideSlideout()
  }

  function onCheck(jobCode: string) {
    if (selectedMatches.includes(jobCode)) {
      const newMatches = selectedMatches.filter((match: string) => match !== jobCode);
      setSelectedMatches(newMatches);
      setIsSelectAllChecked(false)
    } else {
      setSelectedMatches([...selectedMatches, jobCode])
      if ([...selectedMatches, jobCode].length === regressionMatches.length) {
        setIsSelectAllChecked(true)
      }
    }
  }

  const renderMatches = (row: IRow, rowIndex: string) => {
    return (
      <AdvancedRow>
        <AdvancedCell>
          <CheckboxInput checked={selectedMatches.includes(row.original.job_code)} onClick={() => onCheck(row.original.job_code)} />
        </AdvancedCell>
        {COLUMN_HEADERS.map((cell: IColumn) => {
          return (
            <AdvancedCell minWidth={cell.width}>
              {(row.original as any)[cell.accessor]}
            </AdvancedCell>
          )
        })}
      </AdvancedRow>
    );
  }

  const handleSourceSelect = (item: ISelectItem) => {
    const source = surveySourceOptions.find((option: IRegressionSource) => option.value === item.value);
    if (source) {
      setSelectedSurveyJob(undefined);
      setSelectedSurveyDatacut(undefined);
      setSelectedSurveyScope("");
      setRegressionMatches([]);
      setSelectedMatches([]);
      setIsSelectAllChecked(false);
      setIsValidScope(false);
      setIsSaved(false);
      setSelectedSurveySource(source)
      getSurveyJobs(item.value)
    }
  }

  const getRegressionDatacuts = async (jobCode: string, surveyCode: any) => {
    let options = await getRegressionSurveyDatacuts(masterAccountId, { survey_code: surveyCode, job_code: jobCode });
    setSurveyDatacutOptions(options?.data?.map((option) => {
      return { ...option, text: option.pulldown_pretty, value: option.pulldown_pretty }
    }));
  };

  const handleDatacutSelect = (item: ISelectItem) => {
    const datacut = surveyDatacutOptions.find(option => option.value === item.value);
    if (datacut) {
      setSelectedSurveyScope("");
      setRegressionMatches([]);
      setSelectedMatches([]);
      setIsSelectAllChecked(false);
      setIsValidScope(false);
      setSelectedSurveyDatacut(datacut);
      setMinimumValue(datacut?.volume_minimum_pretty || datacut?.volume_minimum);
      setMaximumValue(datacut?.volume_maximum_pretty || datacut?.volume_maximum);
    }
  }

  const handleJobSelect = (item: ISelectItem) => {
    const job = surveyJobOptions.find(option => option.value === item.value);
    if (job) {
      setSelectedSurveyDatacut(undefined);
      setSelectedSurveyScope("");
      setRegressionMatches([]);
      setSelectedMatches([]);
      setIsSelectAllChecked(false);
      setIsValidScope(false);
      setSelectedSurveyJob(job);
      getRegressionDatacuts(job?.job_code, selectedSurveySource?.value)
    }
  }

  // use this functionality to remove commas from values
  const removeCommas = (value: string) => value.replace(/,/g, '');

  const handleScopeChange = (id: string, value: string) => {
    const formatedValue = parseFloat(value);
    setSelectedSurveyScope(value);
    if (selectedSurveyDatacut) {
      const isAllJobs = selectedSurveyJob?.job_code === 'All Jobs';
      const minVal = parseFloat(removeCommas(selectedSurveyDatacut?.volume_minimum_pretty || `${selectedSurveyDatacut?.volume_minimum}`));
      const maxVal = parseFloat(removeCommas(selectedSurveyDatacut?.volume_maximum_pretty || `${selectedSurveyDatacut?.volume_maximum}`));
      const isValidScope = isAllJobs ? !isNaN(formatedValue) : formatedValue >= minVal && formatedValue <= maxVal;
      setIsValidScope(isValidScope);
    }
  }

  const handelSelectAll = () => {
    if (selectedMatches.length !== regressionMatches.length) {
      const newMatches = regressionMatches.map(match => match.job_code);
      setSelectedMatches([...newMatches]);
      setIsSelectAllChecked(true)
    } else {
      setSelectedMatches([])
      setIsSelectAllChecked(false)
    }
  }

  return (
    <React.Fragment>
      {!isSaved ? (
      	<React.Fragment>
          <div className="regression-survey-job__container">
            <SlideoutHeader title="Add Regression Survey Job Match" doClose={onClose}/>
            <ActionableHeaderCard
              cardTitle="Survey Job Details"
              className="regression-survey-job__card"
              isCollapsible={true}
            >
              <div className="row">
                <div className="col-md-6">
                  {renderSelect({
                    label: "Survey Source",
                    id: "survey_source",
                    options: surveySourceOptions,
                    onChange: handleSourceSelect,
                    required: true,
                    value: selectedSurveySource?.value
                  })}
                </div>
                <div className="col-md-6 regression-input_description">{selectedSurveySource?.name}</div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  {renderSelect({
                    className: "grouped-input",
                    label: "Survey Job",
                    id: "survey_job",
                    isDisabled: !selectedSurveySource?.value,
                    options: surveyJobOptions,
                    onChange: handleJobSelect,
                    required: true,
                    value: selectedSurveyJob?.value
                  })}
                  {renderSelect({
                    className: "grouped-input",
                    label: "Data Cut",
                    id: "data_cut",
                    isDisabled: !selectedSurveyJob?.value,
                    options: surveyDatacutOptions,
                    onChange: handleDatacutSelect,
                    required: true,
                    value: selectedSurveyDatacut?.value
                  })}
                </div>
                <div className="col-md-6 regression-input_description">{selectedSurveyJob?.job_description}</div>
              </div>
              <div className="row">
                {renderNumberInput({
                  error: selectedSurveyScope && !isValidScope ? "Please enter a valid scope" : null,
                  label: "Input Scope Value",
                  id: "input_scope_value",
                  isDisabled: !selectedSurveyDatacut?.value,
                  onChange: handleScopeChange,
                  required: true,
                  value: selectedSurveyScope || ''
                })}
                {(selectedSurveyDatacut?.value && selectedSurveyJob?.job_code !== 'All Jobs')  && (
                  <div className="col-md-6 regression-input_description">
                    {`${selectedSurveyDatacut.input_scope_pretty}  Minimum: ${ minimumValue }  Maximum: ${ maximumValue }`}
                  </div>
                )}
              </div>
              <div className="row">
                <div className="col-md-12">
                  <Button
                    className={classnames('pxl-btn', { 'pxl-btn-primary': isCalculateEnabled && !isSaveEnabled })}
                    disabled={!isCalculateEnabled}
                    onClick={() => calculateValues(false, false)}
                  >
                    Calculate Values
                  </Button>
                </div>
              </div>
            </ActionableHeaderCard>
            <ActionableHeaderCard
              cardTitle="Survey Job Matches"
              className="regression-survey-job__card"
              isCollapsible={true}
            >
              <div className="row">
                {regressionMatches?.length > 0 && (
                  <div className="col-md-12 regression-matches__container">
                    <AdvancedTable
                      columnHeaders={COLUMN_HEADERS}
                      getDataRow={renderMatches}
                      rowData={regressionMatches}
                      useSelectAll={true}
                      isAllChecked={isSelectAllChecked}
                      onSelectAll={handelSelectAll}
                      stickyHeader={true}
                      staticTable={true}
                    />
                  </div>
                )}
              </div>
            </ActionableHeaderCard>
          </div>
          <SlideoutFooter isBoxShadowTheme={true}>
            <a className='active footer__action' onClick={onClose}>Cancel</a>
            <Button
              className={classnames('pxl-btn pxl-btn-secondary footer__action', { disabled: !isSaveEnabled })}
              onClick={() => calculateValues(true, false)}
            >
              Save
            </Button>
            <Button
              className={classnames('pxl-btn pxl-btn-primary footer__action', { disabled: !isSaveEnabled })}
              onClick={() => calculateValues(true, true)}
            >
              Save and Submit
            </Button>
          </SlideoutFooter>
        </React.Fragment>
      ) : (
        <div className="regression-survey-job__container">
          <SlideoutSubmitSuccess
            actionFunction={onClose}
            actionText="Done"
            closeAction={onClose}
            submitSuccessDetails='The selected survey job(s) have been created and added to your pricing.'
            submitSuccessTitle='Job details have been saved!'
          />
        </div>
      )}
    </React.Fragment>
  );
}

export default RegressionSurveyJobMatch;